import * as React from 'react';
import { AlertTriangle, Info, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Letter tile used as the identity mark for orgs / apps (design `.mark`). */
export function MarkTile({
  name,
  size = 'md',
  className,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizes = {
    sm: 'h-7 w-7 text-[12px] rounded-[7px]',
    md: 'h-8 w-8 text-[13px] rounded-[8px]',
    lg: 'h-12 w-12 text-[18px] rounded-[10px]',
  } as const;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center border border-border bg-foreground/[0.05] font-mono font-semibold uppercase text-foreground',
        sizes[size],
        className
      )}
    >
      {(name.trim().charAt(0) || '?').toUpperCase()}
    </span>
  );
}

/** Centered empty state with icon, title, body and optional action. */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-foreground/[0.04] text-muted-foreground">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <h3 className="text-[14.5px] font-semibold">{title}</h3>
      {body && <p className="mt-1 max-w-[46ch] text-[13px] text-muted-foreground">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Inline notice. `warn` and `bad` carry a status tint, never the accent. */
export function Notice({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: 'info' | 'warn' | 'bad';
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'border-border bg-card',
    warn: 'border-warning/40 bg-warning/[0.08]',
    bad: 'border-destructive/40 bg-destructive/[0.06]',
  } as const;
  const Icon = tone === 'info' ? Info : AlertTriangle;
  const iconTone = tone === 'warn' ? 'text-warning' : tone === 'bad' ? 'text-destructive' : 'text-muted-foreground';
  return (
    <div
      role={tone === 'info' ? 'status' : 'alert'}
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', tones[tone], className)}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconTone)} />
      <div className="min-w-0 flex-1 text-[13px]">
        <p className="font-semibold">{title}</p>
        {children && <div className="mt-0.5 text-muted-foreground">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Relative time ("5m ago", "3d ago"); falls back to a date after 30 days. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '—';
  if (diff < MIN) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MIN)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)}d ago`;
  return fmtDate(iso);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Shared table/row styling for dense lists. */
export const rowLink =
  'cursor-pointer transition-colors hover:bg-foreground/[0.03] focus-within:bg-foreground/[0.03]';
